import { Backdrop, Grow, Modal } from '@material-ui/core';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Timezone from '../../commons/timezone/Timezone.js';
import { getTicketTipoComponent } from '../../commons/utils/Multilanguage.js';

/**
 * VEC-4067: tickets asociados a una factura de la grilla, cada uno como link a su vista detallada
 * (se abre en una pestaña nueva). Se muestra al hacer clic en la pill "Tickets N" de la columna
 * Ticket, que aparece cuando la factura tiene más de un ticket.
 *
 * Mismo estilo que el detalle "Subregiones" de Período de Presupuestos (sin filtro ni bullets), con las
 * columnas ID Ticket, Tipo, Creado, Servicio y Dominio.
 *
 * @param props.open         si el modal está abierto
 * @param props.factura      fila de la grilla: { nroPrefacturacion, tipo, puntoDeVenta, numero,
 *                           gerenciador: { razonSocial }, tickets: [ids],
 *                           ticketsDetalle: [{ id, tipo, fechaAlta, servicio, dominio }] }
 * @param props.handleClose  cierra el modal
 */
const FacturasTicketsModal = (props) => {
    const factura = props.factura || {};
    const ids = Array.isArray(factura.tickets) ? factura.tickets : [];
    const detalle = Array.isArray(factura.ticketsDetalle) ? factura.ticketsDetalle : [];
    // Un ticket sin datos de detalle (no debería pasar) igual se lista, con el número como link.
    const tickets = ids.map((ticketId) => {
        const datos = detalle.filter((d) => d.id === ticketId)[0];
        return datos || { id: ticketId, tipo: null, fechaAlta: null, servicio: null, dominio: null };
    });

    // Con prefacturación habilitada la factura se identifica por su Nro. de prefacturación; si no, por su tipo.
    // Nro. y Pto. de venta se muestran siempre que existan.
    const identificador = factura.nroPrefacturacion ? factura.nroPrefacturacion : factura.tipo;
    const proveedor = factura.gerenciador && factura.gerenciador.razonSocial ? factura.gerenciador.razonSocial : '';

    const fechaLegible = (fecha) => (fecha ? Timezone.getDateForClient(fecha, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY') : '');

    return (
        <Modal
            open={!!props.open}
            onClose={() => props.handleClose()}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Grow in={!!props.open}>
                <div className="" tabIndex="-1" id="facturas_tickets_modal" role="dialog" aria-labelledby="facturasTicketsModalLabel">
                    <div className="modal-dialog modal-lg" role="document">
                        {/* El diálogo no supera el alto de la ventana: el encabezado y el pie quedan fijos y el cuerpo (datos de la
                            factura + tabla) es lo que se desplaza, así que "Cerrar" siempre está a la vista, con cualquier alto de pantalla. */}
                        <div className="modal-content" style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 3.5rem)' }}>
                            <div className="modal-header bg-fleet">
                                <h5 className="modal-title text-white" id="facturasTicketsModalLabel">
                                    <FormattedMessage id="facturasGrid.render.tickets_modal.header_tickets_asociados" defaultMessage=" Tickets asociados" />
                                </h5>
                                <button type="button" onClick={() => props.handleClose()} className="close text-white" aria-label="Close">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <div className="modal-body" style={{ overflowY: 'auto', minHeight: 0 }}>
                                <p className="mb-1">
                                    <span className="text-muted"><FormattedMessage id="facturasGrid.render.tickets_modal.label_factura" defaultMessage="Factura" /></span> <strong>{identificador}</strong>
                                    {factura.numero && (
                                        <React.Fragment>
                                            {' · '}<span className="text-muted"><FormattedMessage id="facturasGrid.render.tickets_modal.label_numero" defaultMessage="Nro." /></span> <strong>{factura.numero}</strong>
                                        </React.Fragment>
                                    )}
                                    {factura.puntoDeVenta && (
                                        <React.Fragment>
                                            {' · '}<span className="text-muted"><FormattedMessage id="facturasGrid.render.tickets_modal.label_punto_de_venta" defaultMessage="Pto. de venta" /></span> <strong>{factura.puntoDeVenta}</strong>
                                        </React.Fragment>
                                    )}
                                    {proveedor && (
                                        <React.Fragment>
                                            <br />
                                            <span className="text-muted"><FormattedMessage id="facturasGrid.render.tickets_modal.label_proveedor" defaultMessage="Proveedor" /></span> <strong>{proveedor}</strong>
                                        </React.Fragment>
                                    )}
                                </p>
                                <table className="table table-sm table-hover">
                                    <thead style={{ backgroundColor: '#241b3e', color: '#fff' }}>
                                        <tr>
                                            <th><FormattedMessage id="facturasGrid.render.tickets_modal.col_id_ticket" defaultMessage="ID Ticket" /></th>
                                            <th><FormattedMessage id="facturasGrid.render.tickets_modal.col_tipo" defaultMessage="Tipo" /></th>
                                            <th><FormattedMessage id="facturasGrid.render.tickets_modal.col_creado" defaultMessage="Creado" /></th>
                                            <th><FormattedMessage id="facturasGrid.render.tickets_modal.col_servicio" defaultMessage="Servicio" /></th>
                                            <th><FormattedMessage id="facturasGrid.render.tickets_modal.col_dominio" defaultMessage="Dominio" /></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tickets.map((ticket) => (
                                            <tr key={ticket.id}>
                                                <td><a href={'/tickets/' + ticket.id} target="_blank" rel="noopener noreferrer">{ticket.id}</a></td>
                                                <td>{ticket.tipo ? getTicketTipoComponent(ticket.tipo) : ''}</td>
                                                <td>{fechaLegible(ticket.fechaAlta)}</td>
                                                <td>{ticket.servicio || ''}</td>
                                                <td>{ticket.dominio || ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-danger" onClick={() => props.handleClose()}>
                                    <i className="fa fa-times-circle"></i><FormattedMessage id="facturasGrid.render.tickets_modal.cerrar" defaultMessage=" Cerrar" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Grow>
        </Modal>
    );
};

export default FacturasTicketsModal;
